import { useLayoutEffect, useRef, useState } from "react";
import { Icon, Text } from "@ttc3k/trekker";
import { ShoppingBag } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { AssetImage, Product } from "gql/generated";
import { SmartImage } from "shared/components";
import { getValueImages } from "shared/merchCatalog";
import { formatPrice, getMultilingualString } from "shared/utils";
import { Box, Center, Flex, styled } from "styled-system/jsx";
import { useBookerContext } from "widgets/booker/contexts";
import { getCatalogProductSummary } from "./merchCatalogUtils";
import { StockBadge } from "./StockBadge";
import { ColourDots } from "./ColourDots";

type MerchProductCardProps = {
  product: Product;
  cartVariantQuantities: Record<string, number>;
  /** `valueId` is the colour dot being hovered, so details can open on its photo. */
  onSelect: (productId: string, valueId: string | null) => void;
};

export const MerchProductCard = ({
  product,
  cartVariantQuantities,
  onSelect,
}: MerchProductCardProps) => {
  const { language } = useBookerContext();
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_CATALOG" });
  const {
    lowestPrice,
    hasPriceRange,
    isSoldOut,
    quantityInCart,
    image,
    remaining,
    stockSignal,
  } = getCatalogProductSummary(product, cartVariantQuantities);

  // SmartImage sizes in pixels, and the card is a responsive square.
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameSize, setFrameSize] = useState(0);
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const update = () => setFrameSize(Math.floor(el.clientWidth));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [isHovered, setIsHovered] = useState(false);
  const [hasHovered, setHasHovered] = useState(false);
  const [hoveredValueId, setHoveredValueId] = useState<string | null>(null);

  const secondImage = (product.images?.[1] ?? null) as Partial<AssetImage> | null;
  const hoverImage = hoveredValueId
    ? (getValueImages(product, hoveredValueId)[0] ?? image)
    : secondImage;
  const showHoverImage =
    isHovered && !!hoverImage && hoverImage._id !== image?._id;

  const name = getMultilingualString(product.name, language);
  const priceLabel = hasPriceRange
    ? t("FROM", { price: `$${formatPrice(lowestPrice)}` })
    : `$${formatPrice(lowestPrice)}`;

  return (
    <styled.button
      type="button"
      onClick={() => onSelect(product._id, hoveredValueId)}
      onMouseEnter={() => {
        setIsHovered(true);
        setHasHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setHoveredValueId(null);
      }}
      display="flex"
      flexDirection="column"
      gap="150"
      textAlign="left"
      cursor="pointer"
      bg="transparent"
      p="0"
      border="none"
      opacity={isSoldOut && quantityInCart === 0 ? 0.6 : 1}
      minW="0"
      _focusVisible={{ outline: "2px solid", outlineColor: "border.dark" }}
      aria-label={name}
    >
      <Box
        ref={frameRef}
        position="relative"
        w="full"
        aspectRatio="1"
        borderRadius="200"
        overflow="hidden"
        bg="bg.wa.lightest"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={quantityInCart > 0 ? "border.dark" : "border.light"}
      >
        {image ? (
          frameSize > 0 && (
            <>
              <SmartImage
                image={image}
                width={frameSize}
                height={frameSize}
                shape="square"
                alt={name}
              />
              {hasHovered && hoverImage && (
                <Box
                  position="absolute"
                  inset="0"
                  transitionDuration="normal"
                  transitionProperty="opacity"
                  pointerEvents="none"
                  style={{ opacity: showHoverImage ? 1 : 0 }}
                  aria-hidden
                >
                  <SmartImage
                    image={hoverImage}
                    width={frameSize}
                    height={frameSize}
                    shape="square"
                  />
                </Box>
              )}
            </>
          )
        ) : (
          <Center w="full" h="full" aria-label={t("NO_IMAGE")}>
            <Icon Element={ShoppingBag} color="icon.light" size="lg" />
          </Center>
        )}
        <Flex position="absolute" top="100" left="100">
          <StockBadge
            signal={stockSignal}
            remaining={remaining}
            quantityInCart={quantityInCart}
          />
        </Flex>
      </Box>
      <Flex flexDirection="column" gap="25" minW="0">
        <Text visual="bodySemiBold" color="text.dark" lineClamp={2}>
          {name}
        </Text>
        <Text visual="smallMedium" color="text.mid">
          {priceLabel}
        </Text>
        <ColourDots
          product={product}
          activeValueId={hoveredValueId}
          onHoverValue={setHoveredValueId}
        />
      </Flex>
    </styled.button>
  );
};
